<template>
    <CollapsedDropdown
        v-if="collapsed"
        icon="user"
        :title="currentUserName ? `User: ${currentUserName}` : 'User'"
    >
        <template #default="{ close }">
            <a
                v-for="user in filteredUsers"
                :key="user.id"
                class="cd-item"
                :class="{ active: user.id === selectedUser }"
                href="#"
                @click.prevent="
                    selectAndApply(user.id);
                    close();
                "
            >
                {{ user.first_name }}
            </a>
        </template>
    </CollapsedDropdown>
    <div v-else-if="topbar" class="dropdown user-switcher-menu">
        <button
            type="button"
            class="user-switcher-menu__trigger"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            :aria-label="currentUserName ? `Active user: ${currentUserName}` : 'Choose active user'"
            data-cy="easy-user-switcher"
        >
            <i class="fas fa-user-friends user-switcher-menu__icon" aria-hidden="true"></i>
            <span class="user-switcher-menu__name">{{ currentUserName || "Choose user" }}</span>
            <i class="fas fa-chevron-down user-switcher-menu__caret" aria-hidden="true"></i>
        </button>

        <div class="dropdown-menu user-switcher-menu__panel">
            <span class="user-switcher-menu__label">Active user</span>
            <button
                v-for="user in filteredUsers"
                :key="user.id"
                type="button"
                class="dropdown-item user-switcher-menu__item"
                :class="{ active: user.id === selectedUser }"
                @click="selectAndApply(user.id)"
            >
                {{ displayName(user) }}
                <i
                    v-if="user.id === selectedUser"
                    class="fas fa-check user-switcher-menu__check"
                    aria-hidden="true"
                ></i>
            </button>
            <span v-if="filteredUsers.length === 0" class="user-switcher-menu__empty">
                No users at this location
            </span>
        </div>
    </div>
    <div
        v-else
        :class="{
            'sidebar-context-select': useCustomDropdown,
            'page-header-select': inlineLabel
        }"
    >
        <i
            v-if="inlineLabel"
            :class="['fas', `fa-${inlineIcon}`, 'page-header-select__icon']"
            aria-hidden="true"
        ></i>
        <select
            :id="selectId"
            v-model="selectedUser"
            :class="{
                customdropdown: useCustomDropdown,
                'page-header-select__input': inlineLabel
            }"
            class="form-select"
            :aria-label="inlineLabel ? `${inlineLabel} user` : 'User Select'"
            @change="setUserID"
        >
            <option value="" disabled>Select User</option>
            <option v-for="user in filteredUsers" :key="user.id" :value="user.id">
                {{ user.first_name }}
            </option>
        </select>
        <label v-if="useCustomDropdown" :for="selectId" class="sidebar-switcher-label">
            User
        </label>
    </div>
</template>

<script>
import { useId } from "vue";
import { AdminOverrideError } from "@/classes/Errors";
import { API } from "@/services/apiInstance";
import { emitter } from "@/services/emitter";

import { hasTag } from "@/composables/globalMethods";
import CollapsedDropdown from "./common/CollapsedDropdown.vue";

export default {
    components: { CollapsedDropdown },
    props: {
        useCustomDropdown: {
            type: Boolean,
            default: true // Default value to apply customdropdown class
        },
        collapsed: {
            type: Boolean,
            default: false
        },
        topbar: {
            type: Boolean,
            default: false
        },
        /**
         * Inline mode for use outside the sidebar (top bar, page headers):
         * icon + `page-header-select` styling, matching LocationSwitcher so the
         * context switchers read as one set of controls.
         */
        inlineLabel: {
            type: String,
            default: ""
        },
        inlineIcon: {
            type: String,
            default: "user-friends"
        }
    },
    setup() {
        return { selectId: useId() };
    },
    data() {
        return {
            userLocations: [],
            selectedUser: this.$store.state.userID || this.$store.getters.userID() || "",
            userIDUnwatch: null
        };
    },
    computed: {
        filteredUsers() {
            return this.userList.filter(
                (user) =>
                    user.location_ids?.includes(this.$store.state.locationID) &&
                    !hasTag(user, "DEACTIVATED")
            );
        },
        userList() {
            return this.$store.state.userList;
        },
        currentUserName() {
            const current = this.filteredUsers.find((u) => u.id === this.selectedUser);
            return current ? this.displayName(current) : "";
        }
    },
    async mounted() {
        // Watch for changes in userID and update selectedUser
        this.userIDUnwatch = this.$store.watch(
            (state) => state.userID,
            () => {
                this.selectedUser = this.$store.state.userID;
            }
        );

        await this.getUserAllowedLocations();

        const easy_user_id = localStorage.getItem("easy_user_id");

        if (easy_user_id) {
            this.selectedUser = easy_user_id;
            this.applyUserToStore(easy_user_id);
        } else if (this.$store.state.userID) {
            // Mounting is initialization, not a user switch. In particular, do
            // not emit tawk-user-changed here: the app login flow already sets
            // Tawk identity, and a second switcher can mount at the same time.
            this.selectedUser = this.$store.state.userID;
        }
    },
    unmounted() {
        this.userIDUnwatch?.();
        this.userIDUnwatch = null;
    },
    methods: {
        displayName(user) {
            return [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Unnamed user";
        },
        setUserID() {
            console.log("User ID set to: ", this.selectedUser);

            localStorage.setItem("easy_user_id", this.selectedUser);

            this.applyUserToStore(this.selectedUser);
            emitter.emit("tawk-user-changed", { userID: this.selectedUser });
        },
        /**
         * Only replace the store's user when there is genuinely a different
         * user to switch to. Two things went wrong with the previous
         * unconditional commit:
         *  - userList is usually still empty when this mounts, so `find`
         *    returned undefined and wiped the user AuthService had just loaded,
         *    which blanked every consumer of state.user (DepartmentSwitcher
         *    among them).
         *  - Even on a hit, it swapped the full /users/me object for a lighter
         *    list row, silently dropping fields such as department_ids.
         */
        applyUserToStore(id) {
            const match = this.userList.find((user) => user.id == id);
            if (!match) return;
            if (match.id === this.$store.state.user?.id) return;
            this.$store.commit("setUser", match);
        },
        selectAndApply(id) {
            this.selectedUser = id;
            this.setUserID();
        },
        async getUserAllowedLocations() {
            try {
                // If user is already fetched, get the location_ids from the store
                if (this.$store.state.user) {
                    this.userLocations = this.$store.state.user.location_ids;
                    return;
                }

                let response = await API.User.GetByID(this.$store.getters.userID());
                this.userLocations = response.location_ids;
            } catch (error) {
                if (error == AdminOverrideError) {
                    // User is ADMIN, setting locations to all
                    let response = (await API.Location.List()) || [];
                    this.userLocations = response.map((location) => location.id);
                }
            }
        }
    }
};
</script>

<style scoped>
select.customdropdown {
    color: var(--side-nav-control-text, white);
    background-color: var(--side-nav-control-bg, #1a1a1a);
    border-radius: var(--forkto-radius-control);
    border: 1px solid var(--side-nav-control-border, transparent);
}
select.customdropdown {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: var(--side-nav-select-indicator);
    background-size: 0.75rem;
    background-repeat: no-repeat;
    background-position-x: 90%;
    background-position-y: center;
}

.sidebar-switcher-label {
    color: var(--side-nav-muted, rgba(255, 255, 255, 0.7));
}

.collapsed-switcher-btn {
    background-color: #1a1a1a;
    color: white;
    border: none;
    padding: 0.5rem 0;
}

.collapsed-switcher-btn:hover,
.collapsed-switcher-btn:focus {
    background-color: #2a2a2a;
    color: white;
}

.user-switcher-menu {
    flex: 0 1 auto;
    min-width: 0;
}

.user-switcher-menu__trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    max-width: 11rem;
    min-height: 2rem;
    padding: 0 0.7rem;
    color: var(--forkto-ink-2);
    font-size: 0.8125rem;
    font-weight: 500;
    background: none;
    border: 1px solid transparent;
    border-radius: 999px;
}

.user-switcher-menu__trigger:hover,
.user-switcher-menu__trigger[aria-expanded="true"] {
    color: var(--forkto-ink-1);
    background: var(--forkto-surface);
    border-color: var(--forkto-line);
}

.user-switcher-menu__trigger:focus-visible {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: 1px;
}

.user-switcher-menu__icon {
    flex: 0 0 auto;
    color: var(--forkto-ink-2);
    font-size: 0.75rem;
}

.user-switcher-menu__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-switcher-menu__caret {
    flex: 0 0 auto;
    color: var(--forkto-ink-3);
    font-size: 0.625rem;
}

.user-switcher-menu__panel {
    min-width: 13rem;
    margin-top: 0.375rem;
    padding: 0.5rem;
    background: var(--forkto-surface);
    border: 1px solid var(--forkto-line);
    border-radius: 12px;
    box-shadow: 0 10px 28px rgba(var(--forkto-shadow-rgb), 0.12);
}

.user-switcher-menu__label {
    display: block;
    padding: 0.25rem 0.5rem 0.375rem;
    color: var(--forkto-ink-3);
    font-size: 0.6875rem;
    font-weight: 600;
}

.user-switcher-menu__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 2.25rem;
    padding: 0.4rem 0.5rem;
    color: var(--forkto-ink-2);
    font-size: 0.8125rem;
    border-radius: var(--forkto-radius-compact);
}

.user-switcher-menu__item:hover,
.user-switcher-menu__item:focus-visible {
    color: var(--forkto-ink-1);
    background: var(--forkto-sunk);
}

.user-switcher-menu__item.active,
.user-switcher-menu__item.active:hover,
.user-switcher-menu__item.active:focus-visible {
    color: var(--forkto-ink-1);
    font-weight: 600;
    background: var(--forkto-accent-soft);
}

.user-switcher-menu__check {
    color: var(--forkto-ink-2);
    font-size: 0.6875rem;
}

.user-switcher-menu__empty {
    display: block;
    padding: 0.5rem;
    color: var(--forkto-ink-3);
    font-size: 0.8125rem;
}
</style>
